import { createColumnHelper, type HeaderContext, type CellContext } from "@tanstack/react-table";
import { Button, Stack } from "react-bootstrap";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCircle, faDownload, faHistory, faKey, faUnlock } from "@fortawesome/free-solid-svg-icons";
import { ServiceManager } from "../../../services/ServiceManager";
import PagedFilterTable from "../../../components/table";
import ITransformData from "../../../models/TransformData";
import { useParams } from "react-router-dom";
import { toGuid } from "../../../app/common/helper/Guid";
import CustomModal from "../../../components/modal";
import { useState, useEffect } from "react";
import CustomFieldsModal from "./modal/custom-fields-modal";
import { APIStatusCode, EstablishmentDataType, Permissions} from "../../../app/enums";
import { useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";
import { CRBC_USER_TRUE_VALUE, SOMETHING_WENT_WRONG } from "../../../app/constants";
import MISButton from "../../../components/button";
import MISTooltip from "../../../components/tooltip";
import { usePermissionCheck } from "../../../app/common/helper/Permissions";
import IHistoryData from "../../../models/HistoryData";
import IIdentifiers from "../../../models/Identifiers";
import IdentityFieldsModal from "./modal/identity-fields-modal";
import { GUID } from "../../../app/common/helper/Guid";

interface TransformFinalProps {
    establishmentName: string;
}

interface CrbcJwtPayload {
    crbc?: string;
    [key: string]: unknown;
}

interface PinOperationResult {
    isSuccess: boolean;
    message: string;
}

/** Toggle PIN actions (Generate / Clear) on the Transform Final tab. */
const SHOW_GENERATE_PIN_BUTTON = true;

export default function TransformFinal(props: TransformFinalProps) {

    const { establishmentId } = useParams();
    const auth = useAuth();
    const [isCRBCUser, setIsCRBCUser] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [hasData, setHasData] = useState(false);
    const [tableData, setTableData] = useState<ITransformData[]>([]);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [viewMode, setViewMode] = useState("normal");

    const canManagePeople = usePermissionCheck(Permissions.CanManagePeople);
    const [rowSelection, setRowSelection] = useState({});
    const handleCloseConfirmationModal = () => setShowConfirmationModal(false);

    useEffect(() => {
        if (auth.isAuthenticated) {
            const jwt = jwtDecode<CrbcJwtPayload>(auth.user?.access_token ?? '');
            setIsCRBCUser(jwt?.crbc === CRBC_USER_TRUE_VALUE);
        }
        setRowSelection({})
    }, [auth, viewMode]);
    const [showParentalConsentModal, setShowParentalConsentModal] = useState(false);
    const [parentalConsentModalValue, setParentalConsentModalValue] = useState<string | null>("");
    const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);
    const [crbcId, setCrbcId] = useState<GUID | null>(null);
    const [studentName, setStudentName] = useState<string>();
    const [showIdentityFieldModal, setShowIdentityFieldModal] = useState(false);
    const [identityFieldsData, setIdentityFieldsData] = useState<IIdentifiers | null>(null);

    const columnHelper = createColumnHelper<ITransformData>();
    const columns = [{
            id: 'select',
            header: ({ table }: HeaderContext<ITransformData, unknown>) => (
                <input
                    type="checkbox"
                    checked={table.getIsAllRowsSelected()}
                    ref={el => {
                        if (el) el.indeterminate = table.getIsSomeRowsSelected();
                    }}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({ row }: CellContext<ITransformData, unknown>) => (
                <input
                    type="checkbox"
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
            size: 30,
        },              
        columnHelper.accessor('crbcID', {
            header: () => <span>CRBC ID</span>
        }),
        columnHelper.accessor('userTypeName', {
            header: () => <span>User Type</span>
        }),
        columnHelper.accessor('isActive', {
            header: () => <span>Status</span>,
            cell: ({ row }) => (
                <div className={`p-1 ps-2 pe-2 ${row.original.isActive ? 'queue-completed' : 'queue-notexecuted'}`}>
                    <FontAwesomeIcon icon={faCircle} size="2xs" className="icon" />
                    <span className="ms-2">{row.original.isActive ? 'Active' : 'Disabled'}</span>
                </div>
            )
        }),
        columnHelper.accessor('title', { header: () => <span>Title</span> }),
        columnHelper.accessor('surname', { header: () => <span>Surname</span> }),
        columnHelper.accessor('firstName', { header: () => <span>First Name</span> }),
        columnHelper.accessor('legalSurname', { header: () => <span>Legal Surname</span> }),
        columnHelper.accessor('legalFirstName', { header: () => <span>Legal First Name</span> }),
        columnHelper.accessor('upn', { header: () => <span>UPN</span> }),
        columnHelper.accessor('admissionNumber', {
            header: () => <span>Admission No</span>
        }),
        columnHelper.accessor('staffCode', {
            header: () => <span>Staff Code</span>
        }),
        columnHelper.accessor('year', { header: () => <span>Year</span> }),
        columnHelper.accessor('regClass', { header: () => <span>Reg Class</span> }),
        columnHelper.accessor('dateOfBirth', {
            header: () => <span>Date of Birth</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.dateOfBirth ? format(new Date(row.original.dateOfBirth), "dd/MM/yyyy") : ''}</span>
                </Stack>
            )
        }),
        columnHelper.accessor('gender', {
            header: () => <span>Gender</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.gender === 'M' ? 'Male' : row.original.gender === 'F' ? 'Female' : 'Not Specified'}</span>
                </Stack>
            )
        }),
        columnHelper.accessor('isFsm', {
            header: () => <span>FSM</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.isFsm ? 'Yes' : 'No'}</span>
                </Stack>
            )
        }),
        columnHelper.accessor('dateFsmExpiry', {
            header: () => <span>FSM Expiry</span>,
            cell: ({ row }) => (
                <Stack>
                    <span className="font-dark">
                        {row.original.dateFsmExpiry ? format(new Date(row.original.dateFsmExpiry), "p") : ''}
                    </span>
                    <span>
                        {row.original.dateFsmExpiry ? format(new Date(row.original.dateFsmExpiry), "dd-MM-yyyy") : ''}
                    </span>
                </Stack>
            )
        }),
        columnHelper.accessor('dietaryPreferences', {
            header: () => <span>Dietary Preferences</span>
        }),
        columnHelper.accessor('dietaryNeeds', {
            header: () => <span>Dietary Needs</span>
        }),
        columnHelper.accessor('parentalConsent', {
            header: () => <span>Parental Consent</span>,
            cell: ({ row }) => (
                <span
                    style={{ width: "400px", padding: "0" }}
                    onClick={() => setParentalConsentModalData(row.original.parentalConsent)}
                    className={row.original.parentalConsent ? 'parent-consent-td' : ''}>
                    {row.original.parentalConsent ?? ''}
                </span>
            )
        }),
        columnHelper.accessor('hasPhoto', {
            header: () => <span>Has Photo</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.hasPhoto ? 'Yes' : 'No'}</span>
                </Stack>
            )
        }),
        columnHelper.accessor('photoHash', {
            header: () => <span>Photo Hash</span>
        }),
        columnHelper.accessor('crbcID', {
            id: "identifiers",
            header: () => <span>Identifiers</span>,
            cell: ({ row }) => (
                <div className="text-center">
                    <button
                        className="btn btn-outline-secondary"
                        data-testid={`final-person-identifiers-${row.original.crbcID}`}
                        onClick={() => viewFinalPersonIdentifiers(row.original)}>
                        View
                    </button>
                </div>
            ),
            size: 10,
        }),
        // Custom Fields button
        columnHelper.accessor('crbcID', {
            id: "customFields",
            header: () => <span>Custom Fields</span>,
            cell: ({ row }) => (
                <div className="text-center">
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() =>
                            viewCustomFieldsModal(
                                row.original.crbcID,
                                row.original.firstName ?? "",
                                row.original.surname ?? ""
                            )
                        }>
                        View
                    </button>
                </div>
            ),
            size: 10,
        }),
        columnHelper.accessor('establishmentID', {
            header: () => <span>Establishment ID</span>
        }),
        columnHelper.accessor('misID', {
            header: () => <span>MIS ID</span>
        }),
        columnHelper.accessor('misID2', {
            header: () => <span>MIS ID 2</span>
        }),
        columnHelper.accessor('dateCreated', {
            header: () => <span>Date Created</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.dateCreated ? format(new Date(row.original.dateCreated), "dd/MM/yyyy") : ''}</span>
                </Stack>
            )
        }),
        columnHelper.accessor('dateModified', {
            header: () => <span>Date Modified</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.dateModified ? format(new Date(row.original.dateModified), "dd/MM/yyyy") : ''}</span>
                </Stack>
            )
        }),
        columnHelper.accessor('dateLastActive', {
            header: () => <span>Date Last Active</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.dateLastActive ? format(new Date(row.original.dateLastActive), "dd/MM/yyyy") : ''}</span>
                </Stack>
            )
        }),
        columnHelper.accessor('dateLeaving', {
            header: () => <span>Date Leaving</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.dateLeaving ? format(new Date(row.original.dateLeaving), "dd/MM/yyyy") : ''}</span>
                </Stack>
            )
        }),
            columnHelper.accessor('crbcID', {
            id: "audit",
            header: () => <span>Audit</span>,
            cell: ({ row }) => (
                <div className="text-center">
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() =>
                        {
                            setTableData([]);
                            setHasData(false);
                            setCrbcId(row.original.crbcID);
                            setViewMode("history");
                        }}>
                        View
                    </button>
                </div>
            ),
            size: 10,
        }),
    ];

    function setParentalConsentModalData(modalVal: string | null) {
        setParentalConsentModalValue(modalVal);
        setShowParentalConsentModal(true);
    }

    async function fetchData(options: {
        pageIndex: number;
        pageSize: number;
        searchValue: string
    }) {
        const finalPeopleData = await ServiceManager.EstablishmentService.GetFinalPeopleData(toGuid(establishmentId ?? ""), options.searchValue, options.pageIndex + 1,
            options.pageSize);
        setHasData(finalPeopleData?.length > 0);
        setTableData(finalPeopleData || []);
        return finalPeopleData;
    }  

    const viewCustomFieldsModal = (crbcId: GUID, fName: string, sName: string) => {
        setCrbcId(crbcId);
        setStudentName(`${fName} ${sName}`);
        setShowCustomFieldModal(true);
    }   

    const viewFinalPersonIdentifiers = async (person: ITransformData) => {
        try {
            const identifiers = await ServiceManager.EstablishmentService.GetFinalPersonIdentifiers(establishmentId ?? "", person.crbcID);
            if (!identifiers) {
                ServiceManager.ToastService.showError(SOMETHING_WENT_WRONG);
                return;
            }

            setIdentityFieldsData({
                crbcID: person.crbcID,
                pin: identifiers.pin,
                bioID: identifiers.bioID,
                proxCard: identifiers.proxCard,
                magCard: identifiers.magCard,
                firstName: person.firstName,
                surname: person.surname
            });
            setShowIdentityFieldModal(true);
        } catch {
            ServiceManager.ToastService.showError(SOMETHING_WENT_WRONG);
        }
    };

    const exportFinalPeopleData = async () => {
        setIsExporting(true);
        try {
            const response = await ServiceManager.EstablishmentService.ExportFinalPeopleData(establishmentId ?? "");
            if (response.status === APIStatusCode.NoContent) {
                ServiceManager.ToastService.showError("No data available to export.");
                return;
            }
            else if (response.status === APIStatusCode.NotFound) {
                ServiceManager.ToastService.showError("The establishment does not exist.");
                return;
            }
            else if (response.status !== APIStatusCode.Ok) {
                ServiceManager.ToastService.showError(SOMETHING_WENT_WRONG);
                return;
            }
        
            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers['content-disposition'];
            let filename = `${props.establishmentName}_${format(new Date(), 'yyyyMMdd_HHmmss')}_final.people.csv`; // fallback

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = filename;
            link.href = url;
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
            ServiceManager.ToastService.showSuccess('Export completed successfully');
            
        }
        catch (error: unknown) {
            ServiceManager.ToastService.showError("Export error: " + (error instanceof Error ? error.message : String(error)));
        } finally {
            setIsExporting(false);
        }
    };

    const executePinAction = async (
        serviceFn: (establishmentID: string, crbcIDs: string[]) => Promise<any>,
        actionLabel: string
    ) => {
        const selectedRowIndices = Object.keys(rowSelection);
        const selectedCRBCIDs = selectedRowIndices
            .map(index => tableData[parseInt(index)]?.crbcID)
            .filter(id => id);

        if (selectedCRBCIDs.length === 0) {
            ServiceManager.ToastService.showError(`Please select at least one people to ${actionLabel.toLowerCase()} PIN.`);
            return;
        }

        try {
            const response = await serviceFn(establishmentId ?? "", selectedCRBCIDs);

            if (response.status === APIStatusCode.BadRequest) {
                ServiceManager.ToastService.showError(response.data || "Invalid request. Please check the selected users.");
                return;
            } else if (response.status === APIStatusCode.NotFound) {
                ServiceManager.ToastService.showError("The establishment does not exist.");
                return;
            } else if (response.status !== APIStatusCode.Ok) {
                ServiceManager.ToastService.showError(SOMETHING_WENT_WRONG);
                return;
            }

            const pinResults: PinOperationResult[] = response.data;

            if (pinResults && pinResults.length > 0) {
                const successfulPins = pinResults.filter((result) => result.isSuccess);
                const failedPins = pinResults.filter((result) => !result.isSuccess);

                if (failedPins.length > 0) {
                    const failedMessages = failedPins.map((result) => result.message).join(', ');
                    ServiceManager.ToastService.showError(`Failed to ${actionLabel.toLowerCase()} PIN for ${failedPins.length} user(s): ${failedMessages}`);
                }

                if (successfulPins.length > 0) {
                    ServiceManager.ToastService.showSuccess(`PIN ${actionLabel.toLowerCase()}ed successfully for ${successfulPins.length} user(s).`);
                }

                if (failedPins.length === 0) {
                    setRowSelection({});
                }
            } else {
                ServiceManager.ToastService.showSuccess(`PIN ${actionLabel.toLowerCase()} completed for ${selectedCRBCIDs.length} user(s).`);
                setRowSelection({});
            }
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : String(error);
            ServiceManager.ToastService.showError(`Failed to ${actionLabel.toLowerCase()} PIN: ` + msg);
        }
    };

    const handlePinGenerationforPeople = () =>
        executePinAction(
            (id, ids) => ServiceManager.EstablishmentService.GeneratePinforPeople(id, ids),
            "Generate"
        );

    const handleClearPinforPeople = () =>
        executePinAction(
            (id, ids) => ServiceManager.EstablishmentService.ClearPinforPeople(id, ids),
            "Clear"
        );

    const confirmClearPin = async () => {
        if (isClearing) return;
        setIsClearing(true);
        try 
        {
            await handleClearPinforPeople();
        } 
        finally 
        {
            setShowConfirmationModal(false);
            setIsClearing(false);
        }
    };

    const ClearPinConfirmationModal = () => {
        return (
            <Stack>
                <h5 className="text-center w-200">Clear PIN</h5>
                <p className="text-center">Are you sure you want to clear the PIN for the selected people?</p>
                <Stack direction="horizontal" className="mt-3">
                    <Button variant="popup-btn right-margin10 btn-outline-secondary w-50" onClick={handleCloseConfirmationModal}>
                        Cancel
                    </Button>
                    <Button variant="popup-btn right-margin10 btn-danger w-50" disabled={isClearing} onClick={confirmClearPin}>
                        {isClearing ? "Clearing..." : "Yes, Clear PIN"}
                    </Button>
                </Stack>
            </Stack>
        )
    }

        //#region  History
    const historyColumnHelper = createColumnHelper<IHistoryData>();
    const identityColumnHelper = createColumnHelper<IIdentifiers>();
    const historyColumns = [              
        historyColumnHelper.accessor('crbcID', {
            header: () => <span>CRBC ID</span>
        }),
        historyColumnHelper.accessor('userTypeName', {
            header: () => <span>User Type</span>
        }),
        columnHelper.accessor('isActive', {
            header: () => <span>Status</span>,
            cell: ({ row }) => (
                <div className={`p-1 ps-2 pe-2 ${row.original.isActive ? 'queue-completed' : 'queue-notexecuted'}`}>
                    <FontAwesomeIcon icon={faCircle} size="2xs" className="icon" />
                    <span className="ms-2">{row.original.isActive ? 'Active' : 'Disabled'}</span>
                </div>
            )
        }),
        historyColumnHelper.accessor('title', { header: () => <span>Title</span> }),
        historyColumnHelper.accessor('surname', { header: () => <span>Surname</span> }),
        historyColumnHelper.accessor('firstName', { header: () => <span>First Name</span> }),
        historyColumnHelper.accessor('legalSurname', { header: () => <span>Legal Surname</span> }),
        historyColumnHelper.accessor('legalFirstName', { header: () => <span>Legal First Name</span> }),
        historyColumnHelper.accessor('upn', { header: () => <span>UPN</span> }),
        historyColumnHelper.accessor('admissionNumber', {
            header: () => <span>Admission No</span>
        }),
        historyColumnHelper.accessor('staffCode', {
            header: () => <span>Staff Code</span>
        }),
        historyColumnHelper.accessor('year', { header: () => <span>Year</span> }),
        historyColumnHelper.accessor('regClass', { header: () => <span>Reg Class</span> }),
        historyColumnHelper.accessor('dateOfBirth', {
            header: () => <span>Date of Birth</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.dateOfBirth ? format(new Date(row.original.dateOfBirth), "dd/MM/yyyy") : ''}</span>
                </Stack>
            )
        }),
        historyColumnHelper.accessor('gender', {
            header: () => <span>Gender</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.gender === 'M' ? 'Male' : row.original.gender === 'F' ? 'Female' : 'Not Specified'}</span>
                </Stack>
            )
        }),
        historyColumnHelper.accessor('isFsm', {
            header: () => <span>FSM</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.isFsm ? 'Yes' : 'No'}</span>
                </Stack>
            )
        }),
        historyColumnHelper.accessor('dateFsmExpiry', {
            header: () => <span>FSM Expiry</span>,
            cell: ({ row }) => (
                <Stack>
                    <span className="font-dark">
                        {row.original.dateFsmExpiry ? format(new Date(row.original.dateFsmExpiry), "p") : ''}
                    </span>
                    <span>
                        {row.original.dateFsmExpiry ? format(new Date(row.original.dateFsmExpiry), "dd-MM-yyyy") : ''}
                    </span>
                </Stack>
            )
        }),
        historyColumnHelper.accessor('dietaryPreferences', {
            header: () => <span>Dietary Preferences</span>
        }),
        historyColumnHelper.accessor('dietaryNeeds', {
            header: () => <span>Dietary Needs</span>
        }),
        historyColumnHelper.accessor('parentalConsent', {
            header: () => <span>Parental Consent</span>,
            cell: ({ row }) => (
                <span
                    style={{ width: "400px", padding: "0" }}
                    onClick={() => setParentalConsentModalData(row.original.parentalConsent)}
                    className={row.original.parentalConsent ? 'parent-consent-td' : ''}>
                    {row.original.parentalConsent ?? ''}
                </span>
            )
        }),
        historyColumnHelper.accessor('hasPhoto', {
            header: () => <span>Has Photo</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.hasPhoto ? 'Yes' : 'No'}</span>
                </Stack>
            )
        }),
        historyColumnHelper.accessor('photoHash', {
            header: () => <span>Photo Hash</span>
        }),
        // Custom Fields button
        identityColumnHelper.accessor('crbcID', {
            id: "identityFields",
            header: () => <span>Identity Fields</span>,
            cell: ({ row }) => (
                <div className="text-center">
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() =>
                            viewIdentityFieldsModal({
                                crbcID: row.original.crbcID,
                                pin: row.original.pin ?? "",
                                bioID: row.original.bioID ?? "",
                                proxCard: row.original.proxCard ?? "",
                                magCard: row.original.magCard ?? "",
                                identityCreatedDate: row.original.identityCreatedDate ? format(new Date(row.original.identityCreatedDate), "dd/MM/yyyy") : '',
                                identityModifiedDate: row.original.identityModifiedDate ? format(new Date(row.original.identityModifiedDate), "dd/MM/yyyy") : '',
                                identityModifiedBy: row.original.identityModifiedBy ?? "",
                                firstName: row.original.firstName ?? "",
                                surname: row.original.surname ?? ""
                            })
                        }          
                    >
                        View
                    </button>
                </div>
            )
        }),
        historyColumnHelper.accessor('establishmentID', {
            header: () => <span>Establishment ID</span>
        }),
        historyColumnHelper.accessor('misID', {
            header: () => <span>MIS ID</span>
        }),
        historyColumnHelper.accessor('misID2', {
            header: () => <span>MIS ID 2</span>
        }),
        historyColumnHelper.accessor('dateCreated', {
            header: () => <span>Date Created</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.dateCreated ? format(new Date(row.original.dateCreated), "dd/MM/yyyy") : ''}</span>
                </Stack>
            )
        }),
        historyColumnHelper.accessor('dateModified', {
            header: () => <span>Date Modified</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.dateModified ? format(new Date(row.original.dateModified), "dd/MM/yyyy") : ''}</span>
                </Stack>
            )
        }),
        historyColumnHelper.accessor('modifiedBy', {
            header: () => <span>Modified By</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.modifiedBy ? row.original.modifiedBy : ''}</span>
                </Stack>
            )
        }),       
        historyColumnHelper.accessor('dateLastActive', {
            header: () => <span>Date Last Active</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.dateLastActive ? format(new Date(row.original.dateLastActive), "dd/MM/yyyy") : ''}</span>
                </Stack>
            )
        }),
        historyColumnHelper.accessor('dateLeaving', {
            header: () => <span>Date Leaving</span>,
            cell: ({ row }) => (
                <Stack>
                    <span>{row.original.dateLeaving ? format(new Date(row.original.dateLeaving), "dd/MM/yyyy") : ''}</span>
                </Stack>
            )
        })
    ];

    async function fetchHistoryData(options: {
        pageIndex: number;
        pageSize: number;
        searchValue: string
    }) {
        const historyData = await ServiceManager.EstablishmentService.GetFinalPeopleHistoryData(toGuid(establishmentId ?? ""), options.searchValue, options.pageIndex + 1,
            options.pageSize, crbcId ?? undefined);
        setHasData(historyData?.length > 0);
        setTableData(historyData || []);
        return historyData;
    }
    
    const viewIdentityFieldsModal = (rowData: IIdentifiers) => {
        // Note: intentionally NOT updating crbcId / studentName here.
        // crbcId is part of the history table's react-query key, so mutating it
        // would cause the underlying history table to refetch when the modal opens.
        setIdentityFieldsData({
            crbcID: rowData.crbcID,
            pin: rowData.pin,
            bioID: rowData.bioID,
            proxCard: rowData.proxCard,
            magCard: rowData.magCard,
            identityCreatedDate: rowData.identityCreatedDate,
            identityModifiedDate: rowData.identityModifiedDate,
            identityModifiedBy: rowData.identityModifiedBy,
            firstName: rowData.firstName,
            surname: rowData.surname
        });
        setShowIdentityFieldModal(true);
    };  
    //#endregion

    return (
        <>
            <div id="listView-data">
                <PagedFilterTable
                    key={`${establishmentId}-${viewMode}${viewMode === "history" ? `-${crbcId ?? 'none'}` : ''}`}
                    tableKey={`transform-final-data-${establishmentId}`}
                    columns={viewMode === "history" ? historyColumns : columns}
                    data={[]}
                    usePagination={true}
                    pageRecords={25}
                    fnFetchData={viewMode === "history" ? fetchHistoryData : fetchData}
                    useSearch={true}         
                    enableRowSelection={true}
                    rowSelection={rowSelection}
                    viewMode={viewMode}
                    crbcId={viewMode === "history" ? crbcId : undefined}
                    onRowSelectionChange={setRowSelection}

                    customActions={
                        isCRBCUser && (hasData || viewMode === "history") ? (
                            <div className="d-flex gap-2">
                            {viewMode === "normal" && (
                                <>
                                    <MISTooltip message="Full export from the final.people table for this establishment." placement="top">
                                        <MISButton 
                                            text="Export" 
                                            className="btn btn-primary crbc-bg-color d-flex align-items-center"
                                            disabled={isExporting}
                                            FnOnClick={exportFinalPeopleData}
                                            textClass="ms-0 text-center font-14"
                                            icon={<FontAwesomeIcon icon={faDownload} className="font-14" />}
                                        />
                                    </MISTooltip>

                                    {SHOW_GENERATE_PIN_BUTTON && (
                                        <>
                                            <MISTooltip
                                                message={
                                                    canManagePeople
                                                        ? "Generate PIN for selected people(s)"
                                                        : "Permission required to generate PIN"
                                                }
                                                placement="top">
                                                <div>
                                                    <MISButton 
                                                        text="Generate PIN" 
                                                        className="btn btn-primary crbc-bg-color d-flex align-items-center"
                                                        disabled={!canManagePeople || Object.keys(rowSelection).length === 0}
                                                        FnOnClick={handlePinGenerationforPeople}
                                                        textClass="ms-0 text-center font-14"
                                                        icon={<FontAwesomeIcon icon={faKey} className="font-14" />}
                                                    />
                                                </div>
                                            </MISTooltip>

                                            <MISTooltip
                                                message={
                                                    canManagePeople
                                                        ? "Clear PIN for selected people(s)"
                                                        : "Permission required to clear PIN"
                                                }
                                                placement="top">
                                                <div>
                                                    <MISButton 
                                                        text="Clear PIN" 
                                                        className="btn btn-danger d-flex align-items-center"
                                                        disabled={!canManagePeople || Object.keys(rowSelection).length === 0}
                                                        FnOnClick={() => setShowConfirmationModal(true)}
                                                        textClass="ms-0 text-center font-14"
                                                        icon={<FontAwesomeIcon icon={faUnlock} className="font-14" />}
                                                    />
                                                </div>
                                            </MISTooltip>
                                        </>
                                    )}

                                    <MISTooltip message="View History of people for this establishment." placement="top">
                                        <MISButton 
                                            text="History" 
                                            className="btn btn-primary crbc-bg-color d-flex align-items-center"
                                            FnOnClick={() => {
                                                setViewMode("history")
                                                setCrbcId(null)
                                            }}
                                            textClass="ms-0 text-center font-14"
                                            icon={<FontAwesomeIcon icon={faHistory} className="font-14" />}
                                        />
                                    </MISTooltip>
                                </>
                            )}
                            {viewMode === "history" && (
                                <MISTooltip message="Return to Final People view" placement="top">
                                    <MISButton 
                                        text="Back" 
                                        className="btn btn-primary crbc-bg-color d-flex align-items-center"
                                        FnOnClick={() => setViewMode("normal")}
                                        textClass="ms-0 text-center font-14"
                                        icon={<FontAwesomeIcon icon={faArrowLeft} className="font-14" />}
                                    />
                                </MISTooltip>
                            )}
                            </div>
                        ) : undefined
                    }
                />
            </div>
            <CustomModal isShow={showParentalConsentModal} handleClose={() => setShowParentalConsentModal(false)} header={<p className="heading">Parental Consent</p>} size="md">
                {parentalConsentModalValue}
            </CustomModal>
            <CustomModal isShow={showCustomFieldModal} handleClose={() => setShowCustomFieldModal(false)} header={<span><p className="heading mb-0">Custom Fields</p><p className="font-14 mb-0">{studentName} - {crbcId}</p></span>} size="lg">
                <CustomFieldsModal callFrom={EstablishmentDataType.TransformFinal} crbcId={crbcId} establishmentId={establishmentId} />
            </CustomModal>
            <CustomModal isShow={showConfirmationModal} handleClose={() => setShowConfirmationModal(false)} size="md">
                {ClearPinConfirmationModal()}
            </CustomModal>
            <CustomModal isShow={showIdentityFieldModal} handleClose={() => setShowIdentityFieldModal(false)} header={<span><p className="heading mb-0">Identity Fields</p><p className="font-14 mb-0">{`${identityFieldsData?.firstName ?? ""} ${identityFieldsData?.surname ?? ""}`.trim()} - {identityFieldsData?.crbcID}</p></span>} size="lg">
                {identityFieldsData && <IdentityFieldsModal identifiers={identityFieldsData} showHistoryMetadata={viewMode === "history"} />}
            </CustomModal>            
        </>
    )
}